import React from 'react';
import createRoot from 'react-dom';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { Provider } from 'react-redux';
import store from './app/client-redux/store';
import axiosConfig from './libraries/axiosConfig';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType, BrowserRouter } from 'react-router-dom';
import { ColorSchemeScript, MantineProvider, MantineThemeOverride } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ContextMenuProvider } from 'mantine-contextmenu';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import 'mantine-contextmenu/styles.layer.css';
import { GoogleMapsProvider } from './app/googleMapsProvider';
import './app.scss';
import { QueryClient, QueryClientProvider } from 'react-query';

const customThemes: Record<string, [string, string, string, string, string, string, string, string, string, string]> = {
  zarph: [
    '#003f52', // $zarph-dark-color
    '#004960', // $zarph-color
    '#016273', // $zarph-secondary
    '#47d4ff', // $zarph-light-color
    '#03abac', // $zarph-green
    '#176d88',
    '#3b3b3b',
    '#2e2e2e',
    '#303030',
    '#fcfcfc;' // $zarph-white
  ]
};

const themeOverride: MantineThemeOverride = {
  colors: customThemes,
  breakpoints: {
    xs: '36em',
    sm: '48em',
    md: '62em',
    lg: '75em',
    xl: '87.5em'
  }
};

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  environment: process.env.NODE_ENV,
  integrations: [
    new BrowserTracing({
      routingInstrumentation: Sentry.reactRouterV6Instrumentation(
        React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      )
    })
  ],
  release: process.env.REACT_APP_VERSION,
  tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACE_SAMPLE_RATE)
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
});

axiosConfig();

createRoot.render(
  <React.StrictMode>
    <ColorSchemeScript />
    <MantineProvider theme={themeOverride}>
      <ContextMenuProvider>
        <Notifications />
        <Provider store={store}>
          <GoogleMapsProvider>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </QueryClientProvider>
          </GoogleMapsProvider>
        </Provider>
      </ContextMenuProvider>
    </MantineProvider>
  </React.StrictMode>,
  document.getElementById('root')
);

reportWebVitals();
